import $axios from '@/axios-instance.js'

export default class TwoFactorService {
    baseUrl = '/api/v1/two-factor'

    /**
     * Obtener el estado de 2FA del usuario
     */
    async getStatus() {
        try {
            const response = await $axios.get(`${this.baseUrl}/status`)
            return {
                success: true,
                data: response.data,
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
     * Verificar si el usuario necesita completar el challenge 2FA
     */
    async needsChallenge() {
        try {
            const response = await $axios.get(`${this.baseUrl}/needs-challenge`)
            return {
                success: true,
                data: response.data,
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
     * Habilitar 2FA para el usuario
     */
    async enable(password) {
        try {
            const response = await $axios.post(`${this.baseUrl}/enable`, {
                password: password,
            })
            return {
                success: true,
                data: response.data,
                message: response.data.message || 'Autenticación de dos factores habilitada',
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
   * Confirmar la configuración inicial del 2FA
   */
    async confirm(code) {
        try {
            const response = await $axios.post(`${this.baseUrl}/confirm`, {
                code: code,
            })
            return {
                success: true,
                data: response.data,
                message: response.data.message || 'Autenticación de dos factores confirmada',
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
   * Verificar código 2FA (challenge)
   */
    async challenge(code) {
        try {
            const response = await $axios.post(`${this.baseUrl}challenge`, {
                code: code,
            })
            return {
                success: true,
                data: response.data,
                message: response.data.message || 'Código verificado correctamente',
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
   * Deshabilitar 2FA
   */
    async disable(password) {
        try {
            const response = await $axios.delete(`${this.baseUrl}`, {
                data: { password: password },
            })
            return {
                success: true,
                data: response.data,
                message: response.data.message || 'Autenticación de dos factores deshabilitada',
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
     * Obtener código QR y clave secreta
     */
    async getQRCode() {
        try {
            const response = await $axios.get(`${this.baseUrl}/qr-code`)
            return {
                success: true,
                data: response.data,
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
     * Generar nuevos códigos de recuperación
     */
    async generateRecoveryCodes() {
        try {
            const response = await $axios.post(`${this.baseUrl}/recovery-codes`)
            return {
                success: true,
                data: response.data,
                message: 'Códigos de recuperación generados',
            }
        } catch (error) {
            return this.handleError(error)
        }
    }

    /**
     * Manejo centralizado de errores
     */
    handleError(error) {
        let errorMessage = 'Ha ocurrido un error inesperado'
        let errorData = null
        let statusCode = 0

        if (error.response) {
            // Error de respuesta del servidor
            statusCode = error.response.status
            errorData = error.response.data

            switch (statusCode) {
                case 422:
                    errorMessage = error.response.data.message || 'Datos de entrada inválidos'
                    break
                case 423:
                    errorMessage = 'Se requiere verificación de dos factores'
                    break
                case 401:
                    errorMessage = 'No autorizado'
                    break
                case 400:
                    errorMessage = error.response.data.message || 'Solicitud inválida'
                    break
                case 500:
                    errorMessage = 'Error interno del servidor'
                    break
                default:
                    errorMessage = error.response.data.message || `Error ${statusCode}`
            }
        } else if (error.request) {
            // Error de red
            errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
        } else {
            // Error en la configuración de la petición
            errorMessage = error.message || 'Error en la configuración de la petición'
        }

        return {
            success: false,
            error: true,
            message: errorMessage,
            data: errorData,
            statusCode: statusCode,
        }
    }

    /**
   * Utilidades para validación
   */
    static validateCode(code) {
        // Validar que sea un código de 6 dígitos o código de recuperación
        if (!code || code.length < 6) {
            return {
                valid: false,
                message: 'El código debe tener al menos 6 caracteres',
            }
        }

        // Código TOTP (6 dígitos)
        if (/^\d{6}$/.test(code)) {
            return { valid: true, type: 'totp' }
        }

        // Código de recuperación (8+ caracteres)
        if (code.length >= 8) {
            return { valid: true, type: 'recovery' }
        }

        return {
            valid: false,
            message: 'Formato de código inválido',
        }
    }

    /**
     * Formatear código de entrada
     */
    static formatCode(code) {
        if (!code) return ''

        // Eliminar espacios y convertir a mayúsculas
        const cleanCode = code.replace(/\s/g, '').toUpperCase()

        // Si es un código TOTP, solo números
        if (/^\d+$/.test(cleanCode)) {
            return cleanCode.substring(0, 6)
        }

        // Para códigos de recuperación, mantener formato original
        return cleanCode
    }
}

export const TwoFactorServiceInstance = new TwoFactorService()